import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, createRef, useRef } from 'react';
import FormContainer from "../../../components/FormContainer";
import Gudang from '../../../models/Gudang';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { PageContainerActionParamType } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import Divisi from '../../../models/Divisi';
import { validate as uuidValidate } from "uuid";
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';

const groupName: MenuGroupName = "Stok";
const subgroupName: string = "Master";
const code: MenuCode = "STOCK-GUDANG";
const title: MenuTitle = "Gudang";
const endpoint = "gudang";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<Gudang>({});
    const divisiRef = useRef();
	const navigate = useNavigate();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
			return;

        setLoading(true);

		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
			.then((response) => {
				const data: Gudang | null = response.data?.data ?
				{
					...response.data?.data,
					divisi: response.data.data.ucode_divisi ?
						{
							ucode: response.data.data.ucode_divisi,
							kode: response.data.data.kode_divisi,
							nama: response.data.data.nama_divisi,
							aktif: response.data.data.aktif,
							keterangan: response.data.data.keterangan,
						}
						:
						undefined,
				}
				:
				null;

				setFormData(data? {...data} : {});
			})
			.catch((error) => console.error({error}))
			.finally(() => {
				setLoading(false);
			});
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: Gudang | undefined) => {
        if (!data?.nama)
            return false;

        if (data?.ucode && !data.kode)
            return false;

        if (!data?.ucode_divisi)
            return false;

        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Gudang> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const dataToSend = {
            ucode: param.data.ucode,
            kode: param.data.kode,
            nama: param.data.nama,
			aktif: param.data.aktif,
			alamat: param.data.alamat,
			keterangan: param.data.keterangan,
			ucode_divisi: param.data.ucode_divisi,
        };

		const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(dataToSend)) : apiCaller.post(endpoint, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
		<>
        {
            loading && <Loader />
        }
			<FormContainer
				groupName={groupName}
				subgroupName={subgroupName}
				code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
				<DataForm<Gudang>
					groupName={groupName}
					subgroupName={subgroupName}
					code={code}
					title={title}
					data={formData}
					isViewMode={isViewMode}
					fields={[
						{
							name: "kode",
							title: "Kode Gudang",
							inputType: "text",
							dataType: (typeof formData.kode),
							isReadonly: false,
							isRequired: formData?.ucode ? true : false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Gudang>) => onChanged(valueObj),
						},
						{
							name: "nama",
							title: "Nama Gudang",
							inputType: "text",
							dataType: (typeof formData.nama),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Gudang>) => onChanged(valueObj),
						},
						{
							name: "alamat",
							title: "Alamat",
							inputType: "text",
							dataType: (typeof formData.alamat),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Gudang>) => onChanged(valueObj),
						},					
						{
							name: "divisi",
							keyField: "ucode",
							labelField: "nama",
							editKeyField: "ucode_divisi",
							title: "Divisi",
							inputType: "combobox",
							dataType: (typeof Divisi),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Gudang>) => onChanged(valueObj),
							searchEndpoint: "divisi/nama",
							searchEndpointFilterParamName: "nama",
							ref: divisiRef,
						},
						{
							name: "keterangan",
							title: "Keterangan",
							inputType: "textarea",
							dataType: (typeof formData.keterangan),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Gudang>) => onChanged(valueObj),
							rows: 3,
						},
					]}
					onValidate={onValidate}
					onClose={(pathObject) => onClose(pathObject)}
					onSave={(data) => onSave(data)}
				/>
			</FormContainer>
		</>
    );
};

export default Form;
